/**
 * peeka 아이콘 — 두 갈래로 나뉜다.
 *
 * 1) **PEEKA 픽셀 아웃라인 26종** (assets/icons/peeka/)
 *    duckflex-peeka-docs 의 agent/add-peeka-*-icon 브랜치에서 온 자체 아이콘이다.
 *    원본이 **SVG** 로 온다 (2026-08-19, agent/add-peeka-*-svg 브랜치).
 *    `output_docs/peeka-icons/<이름>/<이름>-{14,18,24,32}.svg` 네 벌인데
 *    metadata 의 `geometry: identical-across-sizes` 그대로 **넷의 path 가 같다**
 *    (30종 전수 확인). width/height 만 다르므로 한 벌만 두고 CSS 로 키운다.
 *
 *    ⚠ 색만 바꾼다 — `#0E0F1A` 고정이라 다크에서 사라진다. → currentColor.
 *
 *    **14×14 진짜 픽셀 격자다.** 이전 1024px PNG 세대와 달리 30종이 규격으로
 *    균일하다 — 잉크 폭 10~11칸, 선 두께 1칸. 그래서 예전처럼 코드에서 크기·
 *    두께를 맞출 필요가 없다 (그 시도는 183c48b 에서 실패하고 되돌렸다).
 *
 *    `shape-rendering="crispEdges"` 를 **켠다.** 한 칸이 sm 20px 에서도 1.43px 라
 *    사라지는 칸이 없고, 픽셀 그림이라 켜는 쪽이 확연히 선명하다.
 *    (48 격자 시절에는 한 칸이 0.58px 라 칸이 통째로 날아가서 껐었다.)
 *
 *    선 두께는 1/14 = **7.1%** 다 — 이전 세대(4.2%)보다 굵어
 *    lg 2.29px · md 1.71px · sm 1.43px 로 전부 1px 을 넘는다.
 * 2) **heroicons 아웃라인 — 이제 `Swatch` 하나뿐이다** (2026-08-27).
 *    나머지는 전부 PEEKA 판으로 옮겨졌다. Swatch(테마 팔레트)는 아직 PEEKA
 *    아이콘이 없어서 남는다 — 문서 저장소에도 팔레트 그림이 없다.
 *    Astryx 가 자기 기본 아이콘을 "themes should override these with a proper icon
 *    library (heroicons, lucide…)" 라고 안내하고, 규격도 24×24 · stroke 1.5 ·
 *    round cap 으로 heroicons 아웃라인과 같다. 그래서 Astryx semantic 이름을
 *    거치지 않고 heroicons 를 바로 쓴다 — 출처가 하나여야 결이 안 갈린다.
 *
 *    이 하나도 **임시**다. PEEKA 팔레트 아이콘이 나오면 1) 로 옮긴다.
 *
 *    ⚠ arrowDown·arrowLeft·arrowRight·arrowUp 은 PEEKA 판이 있었는데 셰브런으로
 *      되돌렸다 (2026-08-14). 그림이 Drop* 와 같은 셰브런이라 두 이름이 같은 모양을
 *      가리키게 됐고, 그럴 바엔 한 출처로 모으는 편이 낫다.
 *
 * size: lg(28) / md(18) / sm(14) — wrapper px.
 * status: default / disabled — disabled = opacity 30%.
 *
 * PEEKA SVG 는 raw 로 import 해 inline 렌더한다 — img 로 로드하면 svg sandbox
 * 때문에 currentColor 가 안 먹는다.
 */
import type { ComponentType, SVGProps } from 'react';

import { SwatchIcon } from '@heroicons/react/24/outline';

import addSvg from '../../assets/icons/peeka/add.svg?raw';
import backSvg from '../../assets/icons/peeka/back.svg?raw';
import cameraSvg from '../../assets/icons/peeka/camera.svg?raw';
import cardSvg from '../../assets/icons/peeka/card.svg?raw';
import checkSvg from '../../assets/icons/peeka/check.svg?raw';
import chevronDownSvg from '../../assets/icons/peeka/chevron-down.svg?raw';
import chevronLeftSvg from '../../assets/icons/peeka/chevron-left.svg?raw';
import chevronRightSvg from '../../assets/icons/peeka/chevron-right.svg?raw';
import chevronUpSvg from '../../assets/icons/peeka/chevron-up.svg?raw';
import closeSvg from '../../assets/icons/peeka/close.svg?raw';
import copySvg from '../../assets/icons/peeka/copy.svg?raw';
import defaultSvg from '../../assets/icons/peeka/default.svg?raw';
import deleteSvg from '../../assets/icons/peeka/delete.svg?raw';
import downloadSvg from '../../assets/icons/peeka/download.svg?raw';
import editSvg from '../../assets/icons/peeka/edit.svg?raw';
import dropdownSvg from '../../assets/icons/peeka/dropdown.svg?raw';
import dropleftSvg from '../../assets/icons/peeka/dropleft.svg?raw';
import droprightSvg from '../../assets/icons/peeka/dropright.svg?raw';
import dropupSvg from '../../assets/icons/peeka/dropup.svg?raw';
import graphDownSvg from '../../assets/icons/peeka/graph-down.svg?raw';
import helpSvg from '../../assets/icons/peeka/help.svg?raw';
import graphUpSvg from '../../assets/icons/peeka/graph-up.svg?raw';
import homeSvg from '../../assets/icons/peeka/home.svg?raw';
import infoSvg from '../../assets/icons/peeka/info.svg?raw';
import batterySvg from "../../assets/icons/peeka/battery.svg?raw";
import lightningSvg from '../../assets/icons/peeka/lightning.svg?raw';
import listSvg from '../../assets/icons/peeka/list.svg?raw';
import minusSvg from '../../assets/icons/peeka/minus.svg?raw';
import moonSvg from '../../assets/icons/peeka/moon.svg?raw';
import mySvg from '../../assets/icons/peeka/my.svg?raw';
import nextSvg from '../../assets/icons/peeka/next.svg?raw';
import notificationSvg from '../../assets/icons/peeka/notification.svg?raw';
import photoSvg from '../../assets/icons/peeka/photo.svg?raw';
import reloadSvg from '../../assets/icons/peeka/reload.svg?raw';
import reportSvg from '../../assets/icons/peeka/report.svg?raw';
import searchSvg from '../../assets/icons/peeka/search.svg?raw';
import settingSvg from '../../assets/icons/peeka/setting.svg?raw';
import shareSvg from '../../assets/icons/peeka/share.svg?raw';
import sortSvg from '../../assets/icons/peeka/sort.svg?raw';
import storageSvg from '../../assets/icons/peeka/storage.svg?raw';
import sunSvg from '../../assets/icons/peeka/sun.svg?raw';
import styles from './Icon.module.css';

export type IconName =
  | 'default'
  | 'Add'
  | 'arrowDown'
  | 'arrowLeft'
  | 'arrowRight'
  | 'arrowUp'
  | 'Back'
  | 'Camera'
  | 'Card'
  | 'Check'
  | 'Close'
  | 'Copy'
  | 'Delete'
  | 'Download'
  | 'Dropdown'
  | 'Dropleft'
  | 'Dropright'
  | 'Dropup'
  | 'Edit'
  | 'GraphDown'
  | 'GraphUp'
  | 'Help'
  | 'Home'
  | 'Info'
  | 'Battery'
  | 'Lightning'
  | 'List'
  | 'Minus'
  | 'Moon'
  | 'My'
  | 'Next'
  | 'Notification'
  | 'Photo'
  | 'Reload'
  | 'Report'
  | 'Search'
  | 'Setting'
  | 'Share'
  | 'Sort'
  | 'Storage'
  | 'Sun'
  | 'Swatch';

export type IconSize = 'lg' | 'md' | 'sm' | 'xs';
export type IconStatus = 'default' | 'disabled';

/**
 * 아이콘 크기 사다리 (2026-08-14).  lg 28 · md 18 · sm 14
 *
 * ⚠ 픽셀 아트는 16칸 격자라 **16의 배수**에서만 한 칸이 정수 px 로 떨어진다.
 *   지금 사다리는 셋 다 배수가 아니다 — 한 칸이 1.75 / 1.125 / 0.875px 다.
 *   crispEdges 를 안 걸어 뒀으므로 칸이 사라지지 않고 가장자리만 부드러워진다.
 *   (crispEdges 를 걸면 반올림에서 칸이 통째로 날아간다 — 그래서 뺐다.)
 */
/**
 * 2026-08-14: 28/18/14 → 32/24/20 으로 올렸다.
 * PEEKA 아웃라인 선이 아이콘 폭의 4.2% 라 18px 에서 0.75px, 14px 에서 0.58px 이었다.
 * 그림을 굵혀서 풀려다 뭉개져서(8f3256c) 크기로 풀었다 — 1.34 / 1.01 / 0.84px.
 * 전부 4배수라 peeka 간격 사다리와도 결이 맞는다.
 * ⚠ 담는 상자도 같이 바꿔야 한다 — BottomNavBar .navIcon, AppBar 버튼 패딩.
 */
/**
 * ⚠ xs(16px)는 2026-08-19 에 더했다. 칩 안의 '빼기(×)' 처럼 **글자에 딸려 붙는**
 *   자리 전용이다 — sm(20)은 12px 글자 옆에서 글자보다 커 보였다.
 *   아이콘 단독으로 서는 자리에는 쓰지 말 것. 손가락 타깃이 안 나온다.
 */
const SIZE_MAP: Record<IconSize, number> = { lg: 32, md: 24, sm: 20, xs: 16 };

/** PEEKA 자체 아이콘 — inline SVG (currentColor 상속). */
const SVG_RAW: Partial<Record<IconName, string>> = {
  default: defaultSvg,
  Add: addSvg,
  Back: backSvg,
  arrowDown: chevronDownSvg,
  arrowLeft: chevronLeftSvg,
  arrowRight: chevronRightSvg,
  arrowUp: chevronUpSvg,
  Camera: cameraSvg,
  Card: cardSvg,
  Check: checkSvg,
  Close: closeSvg,
  Copy: copySvg,
  Delete: deleteSvg,
  Dropdown: dropdownSvg,
  Dropleft: dropleftSvg,
  Dropright: droprightSvg,
  Dropup: dropupSvg,
  Download: downloadSvg,
  Edit: editSvg,
  GraphDown: graphDownSvg,
  GraphUp: graphUpSvg,
  Help: helpSvg,
  Home: homeSvg,
  Info: infoSvg,
  Battery: batterySvg,
  Lightning: lightningSvg,
  List: listSvg,
  Minus: minusSvg,
  Moon: moonSvg,
  My: mySvg,
  Next: nextSvg,
  Notification: notificationSvg,
  Photo: photoSvg,
  Reload: reloadSvg,
  Report: reportSvg,
  Search: searchSvg,
  Setting: settingSvg,
  Share: shareSvg,
  Sort: sortSvg,
  Storage: storageSvg,
  Sun: sunSvg,
};

/** PEEKA 아이콘이 아직 없는 자리 — heroicons 아웃라인으로 임시 대체. */
const COMPONENT_ICONS: Partial<Record<IconName, ComponentType<SVGProps<SVGSVGElement>>>> = {
  Swatch: SwatchIcon,
};

/**
 * heroicons 를 PEEKA 옆에 놓기 위한 광학 보정.
 *
 * 두 세트는 캔버스를 쓰는 비율이 다르다 — PEEKA 는 상자의 **95%**(26종 중앙값)를
 * 채우는데 heroicons 셰브런은 **69%** 뿐이라, 같은 px 로 놓으면 확연히 작아 보인다.
 * 브라우저에서 getBBox 로 재서 각 아이콘을 95% 에 맞춰 키운다.
 *
 * 키우면 획도 같이 굵어지므로 stroke-width 를 함께 낮춘다 — PEEKA 선이 아이콘
 * 폭의 4.2% 라 그 값에 맞췄다 (0.042 × 24 ÷ scale).
 *
 * 값이 없는 이름은 보정 없이 그대로 그린다.
 */
const FALLBACK_FIT: Partial<Record<IconName, { scale: number; stroke: number }>> = {
  Swatch: { scale: 1.174, stroke: 0.86 },
};

const warnedNames = new Set<IconName>();

interface IconProps {
  name: IconName;
  size?: IconSize;
  /** override pixel size (Figma 인스턴스가 컨테이너 크기로 stretch 되는 경우) */
  pixelSize?: number;
  status?: IconStatus;
  /** override CSS color. 미지정 시 currentColor (부모 color 상속) */
  color?: string;
  className?: string;
  ariaLabel?: string;
}

export default function Icon({
  name,
  size = 'lg',
  pixelSize,
  status = 'default',
  color,
  className,
  ariaLabel,
}: IconProps) {
  const px = pixelSize ?? SIZE_MAP[size];
  const isDisabled = status === 'disabled';
  const wrapper = {
    width: px,
    height: px,
    color: color ?? 'currentColor',
    opacity: isDisabled ? 0.3 : 1,
  };

  // 1) PEEKA 자체 아이콘 — raw SVG 를 그대로 심는다.
  const raw = SVG_RAW[name];
  if (raw) {
    return (
      <span
        className={[styles.icon, className].filter(Boolean).join(' ')}
        role="img"
        aria-label={ariaLabel ?? name}
        style={wrapper}
        // assets/icons/peeka/ 의 정적 자원만 들어온다 — 빌드타임 상수라 XSS 위험 없음.
        dangerouslySetInnerHTML={{ __html: raw }}
      />
    );
  }

  // 2) 아직 PEEKA 아이콘이 없는 자리 — heroicons 로 임시 대체.
  const Fallback = COMPONENT_ICONS[name];
  if (Fallback) {
    const fit = FALLBACK_FIT[name];
    return (
      <span
        className={[styles.icon, className].filter(Boolean).join(' ')}
        role="img"
        aria-label={ariaLabel ?? name}
        style={wrapper}
      >
        <Fallback
          aria-hidden
          focusable={false}
          style={
            fit
              ? { transform: `scale(${fit.scale})`, transformOrigin: 'center', strokeWidth: fit.stroke }
              : undefined
          }
        />
      </span>
    );
  }

  // 3) 어느 쪽에도 없는 이름 — 개발 중에만 나온다.
  if (!warnedNames.has(name) && import.meta.env.DEV) {
    warnedNames.add(name);
    // eslint-disable-next-line no-console
    console.warn(`[Icon] '${name}' has no registered icon. Falling back to placeholder.`);
  }
  return (
    <span
      className={className}
      role="img"
      aria-label={ariaLabel ?? name}
      style={{ ...wrapper, display: 'inline-block', borderRadius: 'var(--radius-full)',
        backgroundColor: color ?? 'var(--color-accent)' }}
    />
  );
}
